/**
 * Field Structure Context
 * 
 * This context provides access to the field structure and context management
 * throughout the ticket creation workflow.
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { 
  createFieldStructure, 
  cloneFieldStructure, 
  processFieldsIntoStructure,
  createStructureFromServiceResponse,
  updateStructureForContext
} from '../utils/fieldStructureUtils';
import { CONTEXT_TYPES } from '../utils/fieldContextUtils';
import { isDocumentField } from '../utils/fieldPatternUtils';
import { STATIC_FIELD_CATEGORY_MAP, getStaticDocumentFields } from '../utils/fieldCategoryMapping';

// Create context
export const FieldStructureContext = createContext();

/**
 * Field Structure Provider component
 */
export const FieldStructureProvider = ({ children }) => {
  // State for field structure
  const [fieldStructure, setFieldStructure] = useState(null);
  
  // State for current context
  const [currentContext, setCurrentContext] = useState(CONTEXT_TYPES.DEFAULT);
  
  // State for tracking document fields
  const [documentFields, setDocumentFields] = useState(new Set());
  
  // Initialize field structure
  const initializeStructure = useCallback((serviceId) => {
    const newStructure = createFieldStructure(serviceId);
    setFieldStructure(newStructure);
    return newStructure;
  }, []);
  
  // Process service response
  const processServiceResponse = useCallback((response, serviceId) => {
    const newStructure = createStructureFromServiceResponse(response, serviceId);
    setFieldStructure(newStructure);
    
    // Extract document fields
    if (response.staticFields && Array.isArray(response.staticFields)) {
      const docFields = response.staticFields.filter(field => 
        isDocumentField(field)
      );
      setDocumentFields(new Set(docFields));
    }
    
    return newStructure;
  }, []);
  
  // Update context
  const updateContext = useCallback((context) => {
    setCurrentContext(context);
  }, []);
  
  // Register document field
  const registerDocumentField = useCallback((fieldName) => {
    setDocumentFields(prev => new Set([...prev, fieldName]));
  }, []);
  
  // Get contextual field structure
  const contextualStructure = useMemo(() => {
    if (!fieldStructure) return null;
    return updateStructureForContext(fieldStructure, currentContext);
  }, [fieldStructure, currentContext]);
  
  // Check if field should be shown in section
  const shouldShowFieldInSection = useCallback((fieldName, section) => {
    if (!fieldName) return false;
    
    const isDocument = isDocumentField(fieldName);
    
    // Use centralized static document fields list from fieldCategoryMapping
    const staticDocumentFields = getStaticDocumentFields();
    
    // Default context (no employee selected)
    if (currentContext === CONTEXT_TYPES.DEFAULT) {
      // In default context, only show fields in customer info section
      if (section === 'CUSTOMER_INFO') {
        return true;
      } else if (section === 'DOCUMENTS') {
        // Don't show any fields in documents section in default context
        return false;
      }
    }
    
    // Employee selected context
    if (currentContext === CONTEXT_TYPES.EMPLOYEE_SELECTED) {
      // For static document fields, only show them in one section to avoid duplication
      if (staticDocumentFields.includes(fieldName)) {
        // Only show static document fields in CUSTOMER_INFO section
        if (section === 'CUSTOMER_INFO') {
          return true;
        } else {
          return false; // Don't show in DOCUMENTS section
        }
      }
      
      // For other fields, use the STATIC_FIELD_CATEGORY_MAP to determine the category
      const fieldCategory = STATIC_FIELD_CATEGORY_MAP[fieldName];
      
      if (section === 'CUSTOMER_INFO') {
        // Show fields categorized as PERSONAL_INFO in CUSTOMER_INFO section
        return fieldCategory === 'PERSONAL_INFO' || !isDocument;
      } else if (section === 'DOCUMENTS') {
        // Show document fields in DOCUMENTS section when employee is selected
        // But exclude static document fields which are already shown in CUSTOMER_INFO
        return (fieldCategory === 'DOCUMENTS' || isDocument) && !staticDocumentFields.includes(fieldName);
      }
    }
    
    // Default fallback
    return true;
  }, [currentContext]);
  
  // Get fields for section
  const getFieldsForSection = useCallback((section) => {
    if (!contextualStructure) return [];
    
    // Get all field names
    const allFields = Object.keys(contextualStructure.fieldProperties || {});
    
    // Filter fields that should be shown in this section
    return allFields.filter(fieldName => 
      shouldShowFieldInSection(fieldName, section)
    );
  }, [contextualStructure, shouldShowFieldInSection]);
  
  // Context value
  const value = {
    fieldStructure,
    contextualStructure,
    currentContext,
    documentFields,
    initializeStructure,
    processServiceResponse,
    updateContext,
    registerDocumentField,
    shouldShowFieldInSection,
    getFieldsForSection,
    isDocumentField
  };
  
  return (
    <FieldStructureContext.Provider value={value}>
      {children}
    </FieldStructureContext.Provider>
  );
};

/**
 * Hook to use the field structure context
 */
export const useFieldStructure = () => {
  const context = useContext(FieldStructureContext);
  if (!context) {
    throw new Error('useFieldStructure must be used within a FieldStructureProvider');
  }
  return context;
};

// Also export as default for backward compatibility
export default FieldStructureContext;
